import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useAuth } from 'react-oidc-context';

import { config } from '@/config';
import type { Account } from '@/features/auth/accounts';
import {
  clearAccountStore,
  getAccountStore,
  removeAccount,
  subscribeAccountStore,
  upsertAccount,
} from '@/features/auth/accounts';
import { makeSigninState } from '@/features/auth/signinState';

export interface UseSessionResult {
  /** Every account known in this browser, most recently added last. */
  accounts: Account[];
  /** The account whose tokens are (or were) the live oidc-client-ts session. */
  active: Account | null;
  /** Switches to a known account, or does nothing for an unknown `sub`. */
  switchTo: (sub: string) => void;
  /** Forces the Keycloak login screen so a different person can sign in. */
  addAccount: () => void;
  /** Signs out one account (the active one, by default). */
  signOut: (sub?: string) => Promise<void>;
  /** Signs out every known account and ends the Keycloak SSO session. */
  signOutAll: () => Promise<void>;
}

/**
 * The path (+ query) to return to after a redirect round-trip, read fresh at
 * call time rather than through `useLocation` — that would require a Router
 * ancestor, and this hook is meant to work mounted anywhere, including above
 * the app's `BrowserRouter` (see `AppAuthProvider`).
 */
function currentPath(): string {
  return window.location.pathname + window.location.search;
}

/**
 * `signinRedirect` args for re-authenticating a known account: `login_hint`
 * when its e-mail is known, to pre-fill the login form. `forceLogin` sets
 * `prompt: 'login'`, which is required whenever the target may not be who
 * Keycloak's SSO cookie currently remembers — that cookie tracks a single
 * identity per browser, so without `prompt: 'login'` Keycloak silently
 * re-issues tokens for whoever is already signed in there and ignores
 * `login_hint`, which is what made switching to a different known account
 * silently no-op back to the original one. `exactOptionalPropertyTypes`
 * means an explicit `login_hint: undefined` is not the same as omitting the
 * key.
 */
function reauthArgs(email: string | undefined, forceLogin = false) {
  return {
    ...(email ? { login_hint: email } : {}),
    ...(forceLogin ? { prompt: 'login' } : {}),
    state: makeSigninState(currentPath()),
  };
}

/**
 * The accounts known in this browser and the one currently signed in.
 *
 * Built on the WP-04 fallback (`login_hint`), not the "ideal" design: only
 * one account has a live, refreshable session at a time (`oidc-client-ts`'s
 * own `sessionStorage` entry). `switchTo` and the re-auth branch of
 * `signOut` re-run `signinRedirect` with `login_hint` set to the target
 * account's e-mail, which Keycloak answers without the password screen as
 * long as its SSO session is still valid — see PLAN-FASE1.md "Meerdere
 * accounts › Uitkomst spike" for why this was chosen over the fully
 * concurrent design.
 */
export function useSession(): UseSessionResult {
  const auth = useAuth();

  const store = useSyncExternalStore(subscribeAccountStore, () => getAccountStore(config));

  // The only place a profile is known is the live oidc-client-ts user, so
  // every render that sees a fresh authenticated user folds it into the
  // store and marks it active.
  const profileSub = auth.user?.profile.sub;
  useEffect(() => {
    const profile = auth.user?.profile;
    if (!auth.isAuthenticated || !profile?.sub) return;
    upsertAccount(config, {
      sub: profile.sub,
      ...(typeof profile.name === 'string' ? { name: profile.name } : {}),
      ...(typeof profile.email === 'string' ? { email: profile.email } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, profileSub]);

  const active = store.accounts.find((a) => a.sub === store.activeSub) ?? null;

  const switchTo = useCallback(
    (sub: string) => {
      if (sub === store.activeSub) return;
      const target = store.accounts.find((a) => a.sub === sub);
      if (!target) return;
      // Don't flip `activeSub` here: the live oidc-client-ts session (and
      // everything derived from it, like `/me`) still belongs to the old
      // account until the redirect round-trip actually lands, so writing it
      // now would make the UI briefly claim a switch that hasn't happened —
      // and the profile-sync effect above would then flip it straight back
      // once it sees the still-old `auth.user`. The store is updated for
      // real once `upsertAccount` runs against the newly authenticated
      // profile after the redirect completes.
      void auth.signinRedirect(reauthArgs(target.email, true));
    },
    [auth, store.accounts, store.activeSub],
  );

  const addAccount = useCallback(() => {
    void auth.signinRedirect({ prompt: 'login', state: makeSigninState(currentPath()) });
  }, [auth]);

  const signOut = useCallback(
    async (sub?: string) => {
      const target = sub ?? store.activeSub ?? undefined;
      if (!target) return;

      if (target === store.activeSub) {
        // Only the active account has live tokens to revoke — an inactive
        // account's tokens were never held here under the fallback design.
        try {
          await auth.revokeTokens();
        } catch {
          // Revocation is best-effort: still drop it locally either way.
        }
        await auth.removeUser();
      }

      const next = removeAccount(config, target);

      if (target !== store.activeSub) return;

      const nextActive = next.accounts.find((a) => a.sub === next.activeSub);
      if (nextActive) {
        // Same reasoning as `switchTo`: the account just signed out of may
        // still be who Keycloak's SSO cookie remembers, so force the login
        // prompt rather than risk silently landing back on it.
        void auth.signinRedirect(reauthArgs(nextActive.email, true));
      } else {
        // Last account signed out: end the Keycloak SSO session too.
        void auth.signoutRedirect({ post_logout_redirect_uri: window.location.origin });
      }
    },
    [auth, store.activeSub],
  );

  const signOutAll = useCallback(async () => {
    try {
      await auth.revokeTokens();
    } catch {
      // Best-effort, same as signOut.
    }
    clearAccountStore(config);
    void auth.signoutRedirect({ post_logout_redirect_uri: window.location.origin });
  }, [auth]);

  return { accounts: store.accounts, active, switchTo, addAccount, signOut, signOutAll };
}
